// @ts-nocheck
import { useState } from 'react';
import { Stack, Title, Text as MantineText, Code, Group, Button as MantineButton, Divider } from '@mantine/core';
import { Card, HtmlEditor, CompactHtmlEditor } from '../../../components/ui';
import { FileUpload, type UploadedFile } from '../../../components/advanced';
import { IconDownload, IconCode } from '@tabler/icons-react';

export default function EditorPage() {
  const [editorHtml, setEditorHtml] = useState('<p>Welcome to the <strong>HTML Editor</strong>!</p>');
  const [editorText, setEditorText] = useState('');
  const [compactEditorHtml, setCompactEditorHtml] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [showHtml, setShowHtml] = useState(false);
  const [showText, setShowText] = useState(false);

  // Simulated upload handler
  const handleUpload = async (file: File): Promise<{ url: string }> => {
    // Simulate upload delay
    await new Promise((resolve) => setTimeout(resolve, 2000));

    // Return a simulated URL
    return { url: `https://example.com/uploads/${file.name}` };
  };

  return (
    <Stack gap="xl">
      {/* Page Header */}
      <Stack gap="xs">
        <Title order={2}>HTML Editor & File Upload</Title>
        <MantineText c="dimmed" size="sm">
          Rich text editor with HTML/plain text output and file upload components
        </MantineText>
      </Stack>

      {/* HTML Editor */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>HTML Editor</Title>
            <MantineText size="sm" c="dimmed">
              Full-featured WYSIWYG editor with keyboard shortcuts, custom styling support, and HTML/plain text output
            </MantineText>
          </Stack>

          <HtmlEditor
            label="Content"
            description="Use the toolbar to format your text. Supports keyboard shortcuts like Ctrl+B for bold, Ctrl+I for italic, etc."
            value={editorHtml}
            onChange={(html, text) => {
              setEditorHtml(html);
              setEditorText(text);
            }}
            placeholder="Start typing your content here..."
            minHeight={300}
            maxHeight={600}
            gradientVariant="primary"
            showToolbar
            toolbarFeatures={{
              bold: true,
              italic: true,
              underline: true,
              strike: true,
              code: true,
              headings: true,
              lists: true,
              blockquote: true,
              codeBlock: true,
              alignment: true,
              textColor: true,
              link: true,
              image: true,
              table: true,
              clearFormatting: true,
              undo: true,
            }}
            contentStyles={`
              .ProseMirror p {
                margin-bottom: 0.5em;
              }

              .ProseMirror strong {
                font-weight: 700;
              }
            `}
          />

          <Divider />

          {/* Output Display */}
          <Stack gap="sm">
            <Group>
              <MantineButton
                variant="light"
                leftSection={<IconCode size={16} />}
                onClick={() => setShowHtml(!showHtml)}
              >
                {showHtml ? 'Hide' : 'Show'} HTML Output
              </MantineButton>
              <MantineButton
                variant="light"
                leftSection={<IconDownload size={16} />}
                onClick={() => setShowText(!showText)}
              >
                {showText ? 'Hide' : 'Show'} Plain Text Output
              </MantineButton>
            </Group>

            {showHtml && (
              <Stack gap="xs">
                <MantineText size="sm" fw={500}>HTML Output:</MantineText>
                <Code block>{editorHtml}</Code>
              </Stack>
            )}

            {showText && (
              <Stack gap="xs">
                <MantineText size="sm" fw={500}>Plain Text Output:</MantineText>
                <Code block>{editorText || 'No text content'}</Code>
              </Stack>
            )}
          </Stack>

          <Divider />

          {/* Usage Example */}
          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Basic Usage:</MantineText>
            <Code block language="tsx">
{`import { HtmlEditor } from '@/components/ui';

function MyComponent() {
  const [content, setContent] = useState('');
  const [plainText, setPlainText] = useState('');

  return (
    <HtmlEditor
      value={content}
      onChange={(html, text) => {
        setContent(html);
        setPlainText(text);
      }}
      placeholder="Start typing..."
      minHeight={300}
    />
  );
}`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Features:</MantineText>
            <MantineText size="sm" component="ul" style={{ paddingLeft: '1.5em' }}>
              <li>Full keyboard shortcut support (Ctrl+B, Ctrl+I, Ctrl+U, etc.)</li>
              <li>Exposes both HTML and plain text output simultaneously</li>
              <li>Custom content styling via <Code>contentStyles</Code> prop</li>
              <li>Configurable toolbar with selective feature enabling</li>
              <li>Color scheme integration for consistent theming</li>
              <li>Support for headings, lists, blockquotes, code blocks</li>
              <li>Link and image insertion</li>
              <li>Table creation and manipulation</li>
              <li>Text alignment and color customization</li>
              <li>Undo/redo functionality</li>
              <li>Works in all modern browsers</li>
              <li>Styles are scoped to editor content, won't affect other page elements</li>
            </MantineText>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Keyboard Shortcuts:</MantineText>
            <MantineText size="sm" component="ul" style={{ paddingLeft: '1.5em' }}>
              <li><Code>Ctrl+B</Code> - Bold</li>
              <li><Code>Ctrl+I</Code> - Italic</li>
              <li><Code>Ctrl+U</Code> - Underline</li>
              <li><Code>Ctrl+Shift+X</Code> - Strikethrough</li>
              <li><Code>Ctrl+E</Code> - Inline code</li>
              <li><Code>Ctrl+Alt+1/2/3</Code> - Heading 1/2/3</li>
              <li><Code>Ctrl+Shift+8</Code> - Bullet list</li>
              <li><Code>Ctrl+Shift+7</Code> - Numbered list</li>
              <li><Code>Ctrl+Shift+B</Code> - Blockquote</li>
              <li><Code>Ctrl+Alt+C</Code> - Code block</li>
              <li><Code>Ctrl+K</Code> - Add link</li>
              <li><Code>Ctrl+Z</Code> - Undo</li>
              <li><Code>Ctrl+Y</Code> - Redo</li>
            </MantineText>
          </Stack>
        </Stack>
      </Card>

      {/* File Upload */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>File Upload</Title>
            <MantineText size="sm" c="dimmed">
              Drag-and-drop file upload with progress tracking and preview support
            </MantineText>
          </Stack>

          <FileUpload
            label="Upload Files"
            description="Drag files here or click to browse. You can upload up to 10 files."
            value={uploadedFiles}
            onChange={setUploadedFiles}
            onUpload={handleUpload}
            maxFiles={10}
            maxSize={10 * 1024 * 1024} // 10MB
            showPreview
            showProgress
            multiple
            gradientVariant="primary"
          />

          <Divider />

          {/* Usage Example */}
          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Basic Usage:</MantineText>
            <Code block language="tsx">
{`import { FileUpload, type UploadedFile } from '@/components/advanced';

function MyComponent() {
  const [files, setFiles] = useState<UploadedFile[]>([]);

  const handleUpload = async (file: File) => {
    // Upload to your server
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch('/api/upload', {
      method: 'POST',
      body: formData,
    });

    const data = await response.json();
    return { url: data.url };
  };

  return (
    <FileUpload
      value={files}
      onChange={setFiles}
      onUpload={handleUpload}
      maxFiles={5}
      maxSize={5 * 1024 * 1024} // 5MB
      showPreview
    />
  );
}`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Features:</MantineText>
            <MantineText size="sm" component="ul" style={{ paddingLeft: '1.5em' }}>
              <li>Drag-and-drop support</li>
              <li>Image preview for uploaded images</li>
              <li>Upload progress tracking</li>
              <li>File type validation</li>
              <li>File size validation</li>
              <li>Multiple file upload</li>
              <li>Error handling and display</li>
              <li>Remove uploaded files</li>
              <li>Customizable file type restrictions</li>
              <li>Success/error status indicators</li>
            </MantineText>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Accept Specific File Types:</MantineText>
            <Code block language="tsx">
{`// Images only
<FileUpload
  accept={['image/png', 'image/jpeg', 'image/gif']}
  ...
/>

// Documents only
<FileUpload
  accept={['application/pdf', 'application/msword']}
  ...
/>

// Multiple types
<FileUpload
  accept={[
    'image/*',
    'application/pdf',
    'text/plain'
  ]}
  ...
/>`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Compact Editor Example */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Compact Editor (Comments/Notes)</Title>
            <MantineText size="sm" c="dimmed">
              Minimal toolbar configuration for simple text editing
            </MantineText>
          </Stack>

          <HtmlEditor
            placeholder="Add a comment..."
            minHeight={120}
            maxHeight={200}
            showToolbar
            toolbarFeatures={{
              bold: true,
              italic: true,
              link: true,
              lists: true,
              undo: true,
              // Disable other features for a simpler toolbar
              underline: false,
              strike: false,
              code: false,
              headings: false,
              blockquote: false,
              codeBlock: false,
              alignment: false,
              textColor: false,
              image: false,
              table: false,
              clearFormatting: false,
            }}
          />

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<HtmlEditor
  placeholder="Add a comment..."
  minHeight={120}
  showToolbar
  toolbarFeatures={{
    bold: true,
    italic: true,
    link: true,
    lists: true,
    undo: true,
    // Disable other features
    headings: false,
    blockquote: false,
    // ... etc
  }}
/>`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Read-Only Editor Example */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Read-Only Mode</Title>
            <MantineText size="sm" c="dimmed">
              Display formatted content without editing capabilities
            </MantineText>
          </Stack>

          <HtmlEditor
            value="<h2>Sample Article</h2><p>This is a <strong>read-only</strong> editor displaying formatted content. Users can view but not edit this content.</p><blockquote>Blockquotes and other formatting are preserved.</blockquote>"
            readOnly
            showToolbar={false}
            minHeight={150}
          />

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<HtmlEditor
  value={content}
  readOnly
  showToolbar={false}
  minHeight={150}
/>`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Compact/Textarea-like Editor Example */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Compact Textarea-like Editor</Title>
            <MantineText size="sm" c="dimmed">
              Appears as a simple textarea with hover controls. Click the edit button or anywhere in the text to expand the full editor with toolbar.
            </MantineText>
          </Stack>

          <CompactHtmlEditor
            value={compactEditorHtml}
            onChange={(html) => setCompactEditorHtml(html)}
            placeholder="Click to add a note or comment..."
            compactHeight={100}
            minHeight={200}
          />

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`import { CompactHtmlEditor } from '@/components/ui';

<CompactHtmlEditor
  value={content}
  onChange={(html) => setContent(html)}
  placeholder="Click to add a note..."
  compactHeight={100}
  minHeight={200}
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Features:</MantineText>
            <MantineText size="sm" component="ul" style={{ paddingLeft: '1.5em' }}>
              <li>Looks like a simple textarea when collapsed</li>
              <li>Shows edit icon button on hover</li>
              <li>Click anywhere to expand into full editor</li>
              <li>Full toolbar appears when editing</li>
              <li>Click "Done" to collapse back to textarea view</li>
              <li>Perfect for comment boxes, notes, and inline editing</li>
              <li>All standard HtmlEditor features when expanded</li>
            </MantineText>
          </Stack>
        </Stack>
      </Card>
    </Stack>
  );
}
